import { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import type { LogisticsReportKey, ReportData, ReportFilters, ReportOptions } from "./report.types";

export const reportUrl = (key: LogisticsReportKey) => `${API_CONFIG.API_URL}/logistics/reports/${key}`;
const emptyOptions: ReportOptions = { entities: [], rubrics: [] };
export function useLogisticsReports(companyId: number | null, warehouseId: number | null, report: LogisticsReportKey, filters: ReportFilters, page: number, pageSize: number, allowed: boolean) {
  const [result, setResult] = useState<{ key: string; data: ReportData } | null>(null);
  const [optionsResult, setOptionsResult] = useState<{ key: string; options: ReportOptions } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  const [optionsFailure, setOptionsFailure] = useState<string | null>(null);
  const [requestCompleted, setRequestCompleted] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const scopeKey = `${companyId || ""}:${warehouseId || ""}:${report}`;
  const serializedFilters = JSON.stringify(filters);
  const selector = `${scopeKey}:${serializedFilters}:${page}:${pageSize}:${reloadVersion}`;
  const generation = useRef(0);
  const key = useMemo(() => `${selector}:${++generation.current}`, [selector]);
  const params = useMemo(() => ({ company_id: companyId, ...(warehouseId ? { warehouse_id: warehouseId } : {}), ...JSON.parse(serializedFilters), page, page_size: pageSize }), [companyId, warehouseId, serializedFilters, page, pageSize]);
  useEffect(() => {
    if (!companyId || !allowed) return;
    const controller = new AbortController(); let current = true;
    apiClient.get(reportUrl(report), { params, signal: controller.signal, timeout: 60000 })
      .then((response: any) => { if (current) { setResult({ key, data: response.data }); setFailure(null); } })
      .catch((error: any) => { if (current && !controller.signal.aborted) setFailure({ key, message: error.response?.data?.message || "Impossible de charger le rapport." }); })
      .finally(() => { if (current) setRequestCompleted(key); });
    return () => { current = false; controller.abort(); };
  }, [companyId, allowed, report, params, key]);
  useEffect(() => {
    if (!companyId || !allowed) return;
    const controller = new AbortController(); let current = true;
    apiClient.get(`${reportUrl(report)}/options`, { params: { company_id: companyId, ...(warehouseId ? { warehouse_id: warehouseId } : {}) }, signal: controller.signal, timeout: 60000 })
      .then((response: any) => { if (current) { setOptionsResult({ key: scopeKey, options: response.data }); setOptionsFailure(null); } })
      .catch(() => { if (current && !controller.signal.aborted) { setOptionsResult({ key: scopeKey, options: emptyOptions }); setOptionsFailure(scopeKey); } });
    return () => { current = false; controller.abort(); };
  }, [companyId, warehouseId, allowed, report, scopeKey, reloadVersion]);
  return { data: result?.key === key ? result.data : null, error: failure?.key === key ? failure.message : null,
    loading: Boolean(companyId && allowed && requestCompleted !== key), options: optionsResult?.key === scopeKey ? optionsResult.options : emptyOptions,
    optionsError: optionsFailure === scopeKey,
    reload: () => setReloadVersion((v) => v + 1) };
}
